import { useEffect, useMemo, useState, useRef } from 'react';
import { ENDPOINTS } from '../../../Utils/Endpoints';
import { Models } from '../../../Utils/ModelsSeguridad';
import ConfirmDeleteDialog from '../../../../security/ConfirmDeleteDialog';
import { normalizeValue } from '../../../../../utils/Texts';
import axios from 'axios';
import Modal from './UsuariosModal';
import UsuariosTabs from './UsuariosTabs';
import sigmaDelete from '../../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../../Pages/img/Editar.png';
import AlertDialog from '../../../../Lms/Components/Shared/AlertDialog';

const PAGE_SIZE = 10;
const nombreTabla = "Catálogo de Usuarios";
const endpoint = ENDPOINTS.usuarios;
const endpointById = ENDPOINTS.usuarioById;
const modelo = Models.usuarios;

const maps = {
  mapCreate: (f) => ({
    idPersona: Number(f.idPersona) || 0,
    nombreUsuario: f.nombreUsuario,
    contrasenaHASH: f.contrasenaHASH,
    salt: f.salt || null,
    ultimoAcceso: f.ultimoAcceso || null,
  }),
  mapUpdate: (id, f) => ({
    idUsuario: id,
    idPersona: Number(f.idPersona) || 0,
    nombreUsuario: f.nombreUsuario,
    contrasenaHASH: f.contrasenaHASH,
    salt: f.salt || null,
    ultimoAcceso: f.ultimoAcceso || null,
  }),
};

export default function Catalog() {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('new');
  const [selectedId, setSelectedId] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ id: 0, name: '' });
  const [editData, setEditData] = useState(null);
  const [editingRowId, setEditingRowId] = useState(null);
  const [page, setPage] = useState(1);
  const [sortByRecent, setSortByRecent] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ open: false, message: '', title: 'Información', variant: 'info' });

  const triggerAlert = (message, variant = 'error', title = 'Error') => {
    setModalOpen(false);
    setDeleteOpen(false);
    setAlertConfig({ open: true, message, variant, title });
  };

  const editorRef = useRef(null);

  useEffect(() => {
    if (modalOpen && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [modalOpen, selectedId]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [resUsuarios, resPersonas] = await Promise.all([
        axios.get(endpoint),
        axios.get(ENDPOINTS.personas),
      ]);

      const personas = (resPersonas.data || []).map((p) => Models.personas(p));
      const usuarios = (resUsuarios.data || []).map((x) => modelo(x));

      const data = usuarios.map((u) => {
        const per = personas.find((p) => p.idPersona === u.idPersona);
        const nombreCompleto = per
          ? `${per.nombre || ''} ${per.apellidoPaterno || ''} ${per.apellidoMaterno || ''}`.trim()
          : '';
        return { ...u, nombreCompleto };
      });

      setRows(data);
    } catch (e) {
      console.error(e);
      triggerAlert('Ocurrió un error al cargar información. Inténtelo más tarde.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let result = [...rows];

    if (q) {
      result = result.filter((r) => {
        const usuario = (r.nombreUsuario || '').toLowerCase();
        const nombre = (r.nombreCompleto || '').toLowerCase();
        const salt = (r.salt || '').toLowerCase();
        const hash = (r.contrasenaHASH || '').toLowerCase();
        return (
          usuario.includes(q) ||
          nombre.includes(q) ||
          salt.includes(q) ||
          hash.includes(q)
        );
      });
    }

    if (sortByRecent) {
      result.sort((a, b) => {
        const dateA = a.ultimoAcceso ? new Date(a.ultimoAcceso).getTime() : 0;
        const dateB = b.ultimoAcceso ? new Date(b.ultimoAcceso).getTime() : 0;
        return dateB - dateA;
      });
    } else {
      // Orden alfabético por nombre completo por defecto
      result.sort((a, b) => (a.nombreCompleto || '').localeCompare(b.nombreCompleto || ''));
    }

    return result;
  }, [rows, query, sortByRecent]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  const handleNew = () => {
    setSelectedId(null);
    setEditData(null);
    setModalMode('new');
    setModalOpen(true);
    setEditingRowId(null);
  };

  const handleEdit = (id) => {
    const r = rows.find(x => x.idUsuario === id);
    if (!r) {
      triggerAlert('No se encontró la fila en memoria.');
      return;
    }
    // Aseguramos que el query no se altere al editar
    setSelectedId(id);
    setEditData(r);
    setModalMode('edit');
    setModalOpen(true);
    setEditingRowId(id);
  };

  const askDelete = (id, name) => {
    setDeleteTarget({ id, name });
    setDeleteOpen(true);
  };

  const doDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(endpointById(deleteTarget.id));
      setDeleteOpen(false);
      setEditingRowId(null);
      await loadData();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.message || e.message || 'Error al eliminar';
      triggerAlert(`Error al eliminar: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (payload, mode) => {
    try {
      setLoading(true);
      if (mode === 'new') {
        const res = await axios.post(endpoint, maps.mapCreate(payload));
        const newId = res?.data?.idUsuario ?? (typeof res?.data === 'number' ? res.data : null);
        if (newId) setEditingRowId(newId);
      } else {
        if (!payload.contrasenaHASH?.trim())
          await axios.put(endpoint, maps.mapUpdate(selectedId, payload));
        else
          await axios.put(endpoint + "/password", maps.mapUpdate(selectedId, payload));

        setEditingRowId(selectedId);
      }
      setModalOpen(false);
      await loadData();
    } catch (e) {
      console.error(e);
      const errorMsg = e.response?.data?.message || 'Ocurrió un error al guardar. Inténtelo más tarde.';
      triggerAlert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const PageBtn = ({ label, onClick, active, disabled }) => (
    <button
      type="button"
      className={`roles-page-btn ${active ? 'is-active' : ''} ${disabled ? 'is-disabled' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      {label}
    </button>
  );

  const pageNumbers = (() => {
    const maxToShow = 5;
    let start = Math.max(1, page - 2);
    let end = Math.min(totalPages, start + maxToShow - 1);
    start = Math.max(1, end - maxToShow + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  })();

  const startIndex = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(page * PAGE_SIZE, filtered.length);

  return (
    <div className="panel-sigma-main">
      <div className="roles-shell">
        <div className="roles-topbar">
          <h2 className="roles-title">{nombreTabla}</h2>

          <input
            id="search-usuarios-catalog"
            name="search-usuarios-catalog"
            className="roles-search"
            placeholder="Buscar por usuario, salt o hash…"
            value={query}
            autoComplete="off"
            data-lpignore="true"
            data-form-type="other"
            readOnly
            onFocus={(e) => e.target.removeAttribute('readonly')}
            onChange={(e) => { setQuery(e.target.value); setPage(1); }}
          />
          <button
            className={`btn-filter ${sortByRecent ? 'is-active' : ''}`}
            onClick={() => { setSortByRecent(!sortByRecent); setPage(1); }}
            title={sortByRecent ? "Quitar filtro de recientes" : "Ordenar por acceso reciente"}
          >
            {sortByRecent ? '🕒 Recientes' : '🕒 Ver Recientes'}
          </button>
          <button className="btn-action" onClick={handleNew}>＋ Nuevo</button>
        </div>

        <div className="roles-table-wrap">
          <div className="roles-table-scroller">
            <table className="roles-table">
              <thead>
                <tr>
                  <th>Nombre de la Persona</th>
                  <th>Usuario</th>
                  <th style={{ textAlign: 'center' }}>Último Acceso</th>
                  <th style={{ textAlign: 'center' }}>Editar</th>
                  <th style={{ textAlign: 'center' }}>Borrar</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((r) => (
                  <tr key={r.idUsuario} className={editingRowId === r.idUsuario ? 'rk-row-active' : ''}>
                    <td>{normalizeValue(r.nombreCompleto)}</td>
                    <td><strong>{normalizeValue(r.nombreUsuario)}</strong></td>
                    <td style={{ textAlign: 'center' }}>
                      {r.ultimoAcceso ? new Date(r.ultimoAcceso).toLocaleString('es-MX', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }) : 'Nunca'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--edit"
                        onClick={() => handleEdit(r.idUsuario)}
                        aria-label="Editar"
                        title="Editar"
                      >
                        <img src={sigmaEdit} alt="" className="rk-ico" />
                      </button>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className="rk-iconbtn rk-iconbtn--del"
                        onClick={() => {
                          setEditingRowId(null);
                          askDelete(r.idUsuario, r.nombreUsuario || 'Usuario');
                        }}
                        aria-label="Borrar"
                        title="Borrar"
                      >
                        <img src={sigmaDelete} alt="" className="rk-ico" />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && paged.length === 0 && (
                  <tr><td colSpan={7}>Sin resultados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="roles-pagination">
          <div className="roles-pagination-left">
            {filtered.length > 0
              ? <span>Mostrando {startIndex}–{endIndex} de {filtered.length}</span>
              : <span>0 resultados</span>}
          </div>
          <div className="roles-pagination-right">
            <PageBtn label="‹" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} />
            {pageNumbers.map(n => (
              <PageBtn
                key={n}
                label={n}
                onClick={() => setPage(n)}
                active={n === page}
              />
            ))}
            <PageBtn
              label="›"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            />
          </div>
        </div>

        {modalOpen && (
          <div className="roles-editor-slot" ref={editorRef}>
            {modalMode === 'new' && (
              <div className="person-tabs-disabled" style={{ marginBottom: 12 }}>
                <span>
                  Selecciona o crea un usuario y pulsa <b>Editar</b> para gestionar sus datos
                  relacionados.
                </span>
              </div>
            )}
            <Modal
              open={modalOpen}
              mode={modalMode}
              data={editData}
              inline
              onClose={() => setModalOpen(false)}
              onSubmit={handleSubmit}
            />
            {modalMode === 'edit' && editingRowId > 0 && (
              <UsuariosTabs usuarioId={editingRowId} onClose={() => setModalOpen(false)} />
            )}
          </div>
        )}
      </div>

      <ConfirmDeleteDialog
        open={deleteOpen}
        roleName={deleteTarget.name}
        onClose={() => setDeleteOpen(false)}
        onConfirm={doDelete}
      />

      <AlertDialog
        open={alertConfig.open}
        title={alertConfig.title}
        message={alertConfig.message}
        variant={alertConfig.variant}
        onClose={() => setAlertConfig({ ...alertConfig, open: false })}
      />
    </div>
  );
}
